// Hook with functions related to studies and filtering based on global filter. Used in Request page, request review, and File page
import React from "react"
import SelectionItem from "components-design-system/form-fields/Select/SelectionItem/SelectionItem"

// apis
import { useGetStudiesList } from "api/hooks/enum"
import { useGetGlobalFilter } from "api/hooks/global"

const useGlobalFilterStudies = () => {
  const { data: studiesList } = useGetStudiesList()
  const { data: globalFilter } = useGetGlobalFilter()

  // Check if a study or request is in the user's global filters, including favorites and studies/requests, for which the user is a team member
  const isGlobalFilterStudy = (studyStringId) => {
    if (globalFilter) {
      return (
        !!(!globalFilter.my_filter.enable && !globalFilter.fav_filter.enable) ||
        !!(globalFilter.my_filter.enable && globalFilter.my_filter.filter_study_ids.find((filterId) => filterId === studyStringId)) ||
        !!(globalFilter.fav_filter.enable && globalFilter.fav_filter.filter_study_ids.find((filterId) => filterId === studyStringId))
      )
    }
    return true
  }

  // get study options with study ID, accounting for global filter
  const studyOptions = React.useMemo(() => {
    if (!studiesList) return []
    // sort studies only if shorter global filter list
    if (globalFilter?.my_filter.enable || globalFilter?.fav_filter.enable) {
      const filtered = studiesList.reduce((acc, studyObj) => {
        if (isGlobalFilterStudy(studyObj.study_id)) {
          acc.push({ value: studyObj.id, label: studyObj.study_id })
        }
        return acc
      }, [])
      return filtered.sort((a, b) => {
        if (a.label < b.label) {
          return -1
        }
        if (b.label < a.label) {
          return 1
        }
        return 0
      })
    } else {
      // all studies not sorted
      return studiesList.map((study) => {
        return { value: study.id, label: study.study_id }
      })
    }
  }, [studiesList, globalFilter])

  // get authoring study options with authoring study ID, accounting for global filter
  const authStudyOptions = React.useMemo(() => {
    if (!studiesList) return []
    // sort studies only if shorter global filter list
    if (globalFilter?.my_filter.enable || globalFilter?.fav_filter.enable) {
      const filtered = studiesList.reduce((acc, studyObj) => {
        if (studyObj.authoring_study_id && isGlobalFilterStudy(studyObj.study_id)) {
          acc.push({ value: studyObj.authoring_study_id, label: studyObj.study_id })
        }
        return acc
      }, [])
      return filtered.sort((a, b) => {
        if (a.label < b.label) {
          return -1
        }
        if (b.label < a.label) {
          return 1
        }
        return 0
      })
    } else {
      // all studies not sorted
      return studiesList.reduce((acc, study) => {
        if (study.authoring_study_id) {
          acc.push({ value: study.authoring_study_id, label: study.study_id })
        }
        return acc
      }, [])
    }
  }, [studiesList, globalFilter])

  // studies select tagRender function to show studies not on global filter as disabled
  const studiesSelectItemRender = ({ label, onClose }) => {
    if (isGlobalFilterStudy(label, globalFilter)) {
      return (
        <SelectionItem
          label={label}
          type="study"
          onClose={onClose}
        />
      )
    }
    return (
      <SelectionItem
        label={label}
        type="study"
        disabled={true}
        disabledTooltip="This study is not on the global filter"
      />
    )
  }

  return {
    isGlobalFilterStudy,
    studyOptions,
    authStudyOptions,
    studiesSelectItemRender,
  }
}

export default useGlobalFilterStudies